import { type LanguageNameType, useLanguageName } from '@faceup/localization'
import { Typography } from '@faceup/ui-base'
import type { Language } from '@faceup/utils'
import { useMemo } from 'react'
import type { FieldValues } from 'react-hook-form'
import { Select, type SelectProps } from './Select'

export type LanguageSelectProps<T extends FieldValues> = SelectProps<T, Language> & {
  // Can´t be omitted due to some TS problem
  options?: never
  allowedLanguages?: Language[]
  primaryLanguageNameType?: LanguageNameType
}

export const LanguageSelect = <T extends FieldValues>({
  primaryLanguageNameType = 'translated',
  allowedLanguages,
  ...props
}: LanguageSelectProps<T>) => {
  const { getLanguageList } = useLanguageName()

  const languages = getLanguageList({ specificLanguages: allowedLanguages })

  const options = useMemo(
    () =>
      languages.map(({ language, nativeName, translatedName }) => {
        const primaryLanguageName =
          primaryLanguageNameType === 'native' ? nativeName : translatedName
        const secondaryLanguageName =
          primaryLanguageNameType === 'native' ? translatedName : nativeName
        return {
          label: (
            <div className='flex flex-col'>
              <Typography.Text>{primaryLanguageName}</Typography.Text>
              <Typography.Text type='secondary' size='small'>
                {secondaryLanguageName}
              </Typography.Text>
            </div>
          ),
          value: language,
        }
      }),
    [languages, primaryLanguageNameType]
  )

  return (
    <Select
      showSearch
      {...props}
      options={options}
      filterOption={(input, option) =>
        languages.some(
          ({ language, nativeName, translatedName }) =>
            option?.value === language &&
            (nativeName.toLowerCase().includes(input.toLowerCase()) ||
              translatedName.toLowerCase().includes(input.toLowerCase()))
        )
      }
    />
  )
}
